import { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { App } from 'antd';
import {
  Upload,
  FileText,
  X,
  ChevronLeft,
  AlertCircle,
  Lightbulb,
  Target,
  TrendingUp,
  Edit3,
} from 'lucide-react';
import {
  optimizeResume,
  validateResumeFile,
  getTargetLevelText,
  getPriorityInfo,
  getCompetitivenessText,
} from '../services/resumeOptimizeService';
import { renderPdfToImage, isPdfFile } from '../utils/pdfToImage';
import type {
  ResumeOptimizeResult,
  TargetLevel,
} from '../types/resumeOptimize';
import ResumeEditorModal from '../components/ResumeEditorModal';
import AuthModal from '../components/AuthModal';
import { auth } from '../services/auth';

// 步骤枚举
enum Step {
  UPLOAD = 'upload',
  RESULT = 'result',
}

// 文件上传区域组件
function UploadArea({
  onFileSelect,
  selectedFile,
  onRemoveFile,
  disabled,
}: {
  onFileSelect: (file: File) => void;
  selectedFile: File | null;
  onRemoveFile: () => void;
  disabled?: boolean;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = useState(false);
  const { message } = App.useApp();

  const handleDrag = useCallback(
    (e: React.DragEvent) => {
      if (disabled) return;
      e.preventDefault();
      e.stopPropagation();
      if (e.type === 'dragenter' || e.type === 'dragover') {
        setDragActive(true);
      } else if (e.type === 'dragleave') {
        setDragActive(false);
      }
    },
    [disabled],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      if (disabled) return;
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);

      const file = e.dataTransfer.files?.[0];
      if (file) {
        const error = validateResumeFile(file);
        if (error) {
          message.error(error);
          return;
        }
        onFileSelect(file);
      }
    },
    [onFileSelect, message, disabled],
  );

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (disabled) return;
      const file = e.target.files?.[0];
      if (file) {
        const error = validateResumeFile(file);
        if (error) {
          message.error(error);
          return;
        }
        onFileSelect(file);
      }
    },
    [onFileSelect, message, disabled],
  );

  if (selectedFile) {
    return (
      <div
        className={`bg-slate-800/50 rounded-xl p-4 border border-slate-700 ${
          disabled ? 'opacity-60' : ''
        }`}
      >
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-lg bg-amber-500/20 flex items-center justify-center flex-shrink-0">
            <FileText className="w-6 h-6 text-amber-400" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white font-medium truncate">
              {selectedFile.name}
            </p>
            <p className="text-slate-500 text-sm">
              {(selectedFile.size / 1024).toFixed(1)} KB
            </p>
          </div>
          {!disabled && (
            <button
              onClick={onRemoveFile}
              className="p-2 text-slate-500 hover:text-amber-400 hover:bg-amber-500/20 rounded-lg transition-colors cursor-pointer"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      onClick={() => !disabled && fileInputRef.current?.click()}
      onDragEnter={handleDrag}
      onDragLeave={handleDrag}
      onDragOver={handleDrag}
      onDrop={handleDrop}
      className={`relative border-2 border-dashed rounded-xl p-10 text-center transition-all duration-200 ${
        disabled
          ? 'border-slate-700 bg-slate-800/30 cursor-not-allowed opacity-60'
          : dragActive
            ? 'border-slate-500 bg-slate-800/50 cursor-pointer'
            : 'border-slate-600 hover:border-slate-500 hover:bg-slate-800/50 cursor-pointer'
      }`}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept=".txt,.md,.docx,.pdf"
        onChange={handleFileChange}
        disabled={disabled}
        className="hidden"
      />
      <Upload className="w-7 h-7 mx-auto mb-3 text-slate-500" />
      <p className="text-white font-medium mb-2">点击或拖拽选择简历</p>
      <p className="text-slate-500 text-sm">
        支持 PDF、DOCX、TXT、MD 格式，最大 10MB
      </p>
    </div>
  );
}

// 评分展示组件
function ScoreDisplay({
  scores,
  isJDMode,
}: {
  scores: ResumeOptimizeResult['scores'];
  isJDMode?: boolean;
}) {
  const scoreItems = [
    { key: 'structure', label: '结构完整性', score: scores.structure },
    { key: 'content', label: '内容质量', score: scores.content },
    { key: 'expression', label: '表达规范', score: scores.expression },
    // 只有在定向评估模式下才显示岗位匹配度进度条
    ...(isJDMode && scores.jdMatch !== undefined
      ? [{ key: 'jdMatch', label: '岗位匹配度', score: scores.jdMatch }]
      : []),
  ];

  // 根据评分获取颜色和等级信息 - 与h5端保持一致
  const getScoreInfo = (score: number) => {
    if (score >= 90) {
      return {
        color: '#22c55e',
        gradient: 'from-green-400 via-green-500 to-green-600',
        label: '卓越',
        glow: 'shadow-green-500/30',
        textColor: 'text-green-400',
      };
    }
    if (score >= 80) {
      return {
        color: '#f59e0b',
        gradient: 'from-amber-400 via-amber-500 to-amber-600',
        label: '优秀',
        glow: 'shadow-amber-500/30',
        textColor: 'text-amber-400',
      };
    }
    if (score >= 70) {
      return {
        color: '#eab308',
        gradient: 'from-yellow-400 via-yellow-500 to-yellow-600',
        label: '良好',
        glow: 'shadow-yellow-500/30',
        textColor: 'text-yellow-400',
      };
    }
    if (score >= 60) {
      return {
        color: '#f97316',
        gradient: 'from-orange-400 via-orange-500 to-orange-600',
        label: '及格',
        glow: 'shadow-orange-500/30',
        textColor: 'text-orange-400',
      };
    }
    return {
      color: '#ef4444',
      gradient: 'from-red-400 via-red-500 to-red-600',
      label: '待提升',
      glow: 'shadow-red-500/30',
      textColor: 'text-red-400',
    };
  };

  const scoreInfo = getScoreInfo(scores.overall);

  // 根据分数获取进度条颜色 - 与h5端保持一致
  const getScoreBarColor = (score: number) => {
    if (score >= 90) return 'bg-green-500';
    if (score >= 80) return 'bg-amber-500';
    if (score >= 70) return 'bg-yellow-500';
    if (score >= 60) return 'bg-orange-500';
    return 'bg-red-500';
  };

  // 根据分数获取文案颜色 - 与h5端保持一致
  const getScoreTextColor = (score: number) => {
    if (score >= 90) return 'text-green-400';
    if (score >= 80) return 'text-amber-400';
    if (score >= 70) return 'text-yellow-400';
    if (score >= 60) return 'text-orange-400';
    return 'text-red-400';
  };

  return (
    <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
      {/* 综合评分 - 与面试报告详情页保持一致的光圈样式 */}
      <div className="text-center mb-6">
        <div className="inline-flex flex-col items-center">
          <div className="relative" style={{ width: 128, height: 128 }}>
            {/* 外发光效果 */}
            <div
              className={`absolute inset-0 rounded-full blur-md opacity-50 bg-gradient-to-br ${scoreInfo.gradient}`}
            />

            <svg
              className="w-full h-full transform -rotate-90 relative z-10"
              style={{ width: 128, height: 128 }}
            >
              {/* 背景圆环 */}
              <circle
                cx={64}
                cy={64}
                r={60}
                stroke="#1E293B"
                strokeWidth={8}
                fill="none"
              />
              {/* 进度圆环 */}
              <circle
                cx={64}
                cy={64}
                r={60}
                stroke={scoreInfo.color}
                strokeWidth={8}
                fill="none"
                strokeDasharray={2 * Math.PI * 60}
                strokeDashoffset={
                  2 * Math.PI * 60 - (scores.overall / 100) * 2 * Math.PI * 60
                }
                strokeLinecap="round"
                className="transition-all duration-1000 ease-out"
              />
            </svg>

            {/* 中心内容 */}
            <div className="absolute inset-0 flex flex-col items-center justify-center z-20">
              <span
                className={`text-4xl font-bold leading-none ${scoreInfo.textColor}`}
              >
                {Math.ceil(scores.overall)}
              </span>
              <span className="text-xs text-slate-400 mt-1">综合评分</span>
            </div>
          </div>
          <div className="mt-3">
            <div
              className={`text-xl font-bold bg-gradient-to-r ${scoreInfo.gradient} bg-clip-text text-transparent`}
            >
              {scoreInfo.label}
            </div>
          </div>
        </div>
      </div>

      {/* 分项评分 - 进度条颜色和文案颜色与h5端保持一致 */}
      <div className="space-y-3">
        {scoreItems.map((item) => (
          <div key={item.key} className="flex items-center gap-3">
            <span className="text-slate-400 text-sm w-24">{item.label}</span>
            <div className="flex-1 h-2 bg-slate-700 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-500 ${getScoreBarColor(
                  item.score,
                )}`}
                style={{ width: `${item.score}%` }}
              />
            </div>
            <span
              className={`text-sm font-medium w-10 text-right ${getScoreTextColor(item.score)}`}
            >
              {item.score}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// 问题列表组件
function IssuesList({ issues }: { issues: string[] }) {
  return (
    <div className="bg-slate-800/50 rounded-xl p-5 border border-slate-700">
      <div className="flex items-center gap-2 mb-4">
        <AlertCircle className="w-5 h-5 text-amber-400" />
        <h3 className="text-white font-medium">主要问题</h3>
        <span className="text-slate-500 text-sm">({issues.length}项)</span>
      </div>
      <ul className="space-y-3">
        {issues.map((issue, index) => (
          <li key={index} className="flex items-start gap-3">
            <span className="flex-shrink-0 w-5 h-5 rounded-full bg-amber-500/20 text-amber-400 text-xs flex items-center justify-center mt-0.5">
              {index + 1}
            </span>
            <span className="text-slate-300 text-sm leading-relaxed">
              {issue}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// 优化建议组件
function SuggestionsList({
  suggestions,
}: {
  suggestions: ResumeOptimizeResult['suggestions'];
}) {
  // 按优先级排序：critical -> 高 -> 中 -> 低
  const priorityOrder = { critical: 0, high: 1, medium: 2, low: 3 };
  const sortedSuggestions = [...suggestions].sort(
    (a, b) => priorityOrder[a.priority] - priorityOrder[b.priority],
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Lightbulb className="w-5 h-5 text-amber-400" />
        <h3 className="text-white font-medium">优化建议</h3>
        <span className="text-slate-500 text-sm">({suggestions.length}条)</span>
      </div>
      {sortedSuggestions.map((suggestion, index) => {
        const priorityInfo = getPriorityInfo(suggestion.priority) || {
          text: '中优先级',
          color: 'text-amber-400',
          bgColor: 'bg-amber-500/20',
        };
        return (
          <div
            key={index}
            className="bg-slate-800/50 rounded-xl p-5 border border-slate-700"
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                <span className="text-white font-medium">
                  {suggestion.category}
                </span>
                <span
                  className={`px-2 py-0.5 rounded text-xs ${priorityInfo.bgColor} ${priorityInfo.color}`}
                >
                  {priorityInfo.text}
                </span>
              </div>
            </div>
            <div className="space-y-3">
              <div>
                <span className="text-slate-500 text-sm">问题：</span>
                <span className="text-slate-300 text-sm">
                  {suggestion.issue}
                </span>
              </div>
              <div>
                <span className="text-slate-500 text-sm">建议：</span>
                <span className="text-slate-300 text-sm">
                  {suggestion.advice}
                </span>
              </div>
              {suggestion.example && (
                <div className="bg-[#0B0F19]/50 rounded-lg p-3 mt-2">
                  <span className="text-slate-500 text-sm block mb-1">
                    参考示例：
                  </span>
                  <p className="text-slate-400 text-sm whitespace-pre-line">
                    {suggestion.example}
                  </p>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// 岗位匹配分析组件
function JDAnalysis({
  analysis,
}: {
  analysis: ResumeOptimizeResult['jdAnalysis'];
}) {
  if (!analysis) return null;

  // 根据匹配度获取颜色 - 与h5端保持一致
  const getMatchColor = (score: number) => {
    if (score >= 90)
      return { color: '#22c55e', bg: 'bg-green-500', text: 'text-green-400' };
    if (score >= 80)
      return { color: '#f59e0b', bg: 'bg-amber-500', text: 'text-amber-400' };
    if (score >= 70)
      return { color: '#eab308', bg: 'bg-yellow-500', text: 'text-yellow-400' };
    if (score >= 60)
      return { color: '#f97316', bg: 'bg-orange-500', text: 'text-orange-400' };
    return { color: '#ef4444', bg: 'bg-red-500', text: 'text-red-400' };
  };

  const matchColor = getMatchColor(analysis.matchPercentage);

  return (
    <div className="bg-slate-800/50 rounded-xl p-5 border border-slate-700">
      <div className="flex items-center gap-2 mb-4">
        <Target className="w-5 h-5 text-emerald-400" />
        <h3 className="text-white font-medium">岗位匹配分析</h3>
      </div>

      {/* 匹配度 - 进度条颜色与光圈保持一致 */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-slate-400 text-sm">整体匹配度</span>
          <span className={`${matchColor.text} font-medium`}>
            {analysis.matchPercentage}%
          </span>
        </div>
        <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
          <div
            className={`h-full ${matchColor.bg} rounded-full transition-all duration-500`}
            style={{ width: `${analysis.matchPercentage}%` }}
          />
        </div>
      </div>

      {/* 关键词覆盖 */}
      <div className="space-y-3 mb-4">
        <div>
          <span className="text-slate-500 text-sm block mb-2">
            已覆盖关键词：
          </span>
          <div className="flex flex-wrap gap-2">
            {analysis.coveredKeywords.length > 0 ? (
              analysis.coveredKeywords.map((keyword, index) => (
                <span
                  key={index}
                  className="px-2 py-1 bg-emerald-500/20 text-emerald-400 rounded text-xs"
                >
                  {keyword}
                </span>
              ))
            ) : (
              <span className="text-slate-500 text-sm">无</span>
            )}
          </div>
        </div>
        <div>
          <span className="text-slate-500 text-sm block mb-2">
            缺失关键词：
          </span>
          <div className="flex flex-wrap gap-2">
            {analysis.missingKeywords.length > 0 ? (
              analysis.missingKeywords.map((keyword, index) => (
                <span
                  key={index}
                  className="px-2 py-1 bg-red-500/20 text-red-400 rounded text-xs"
                >
                  {keyword}
                </span>
              ))
            ) : (
              <span className="text-slate-500 text-sm">无</span>
            )}
          </div>
        </div>
      </div>

      {/* 竞争力评估 */}
      <div className="flex items-center gap-2 mb-3">
        <TrendingUp className="w-4 h-4 text-slate-400" />
        <span className="text-slate-400 text-sm">
          竞争力评估：
          <span className="text-white">
            {getCompetitivenessText(analysis.competitiveness)}
          </span>
        </span>
      </div>

      {/* Gap分析 */}
      <div className="space-y-2">
        <span className="text-slate-500 text-sm block">Gap分析：</span>
        {analysis.gapAnalysis.map((gap, index) => (
          <div key={index} className="flex items-start gap-2">
            <span
              className="text-slate-600 mt-1"
              style={{ position: 'relative', top: '-6px' }}
            >
              •
            </span>
            <span className="text-slate-400 text-sm">{gap}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// 主页面组件
export default function ResumeOptimizePage() {
  const navigate = useNavigate();
  const { message } = App.useApp();

  // 状态管理
  const [currentStep, setCurrentStep] = useState<Step>(Step.UPLOAD);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  // PDF渲染的图片（用于图片型PDF识别）
  const [picFile, setPicFile] = useState<File | null>(null);
  // 评估模式（默认不选中任何模式）
  const [enableJDMatch, setEnableJDMatch] = useState<boolean | null>(null);
  // 定向模式：岗位名称、岗位级别、JD描述
  const [jobName, setJobName] = useState('');
  const [jobLevel, setJobLevel] = useState<TargetLevel | null>(null);
  const [jobDescription, setJobDescription] = useState('');
  const [result, setResult] = useState<ResumeOptimizeResult | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showEditor, setShowEditor] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [authModalMode, setAuthModalMode] = useState<'login' | 'register'>(
    'login',
  );

  // 进入结果页时自动滚动到页面顶部
  useEffect(() => {
    if (currentStep === Step.RESULT) {
      window.scrollTo({ top: 0 });
    }
  }, [currentStep]);

  // 处理文件选择
  const handleFileSelect = useCallback((file: File) => {
    setSelectedFile(file);
    setPicFile(null);
  }, []);

  // 处理文件移除
  const handleRemoveFile = useCallback(() => {
    setSelectedFile(null);
    setPicFile(null);
  }, []);

  // 处理开始评估
  const handleStartAnalyze = async () => {
    if (!selectedFile) {
      message.error('请先上传简历文件');
      return;
    }

    // 检查用户是否已登录
    if (!auth.isAuthenticated()) {
      setAuthModalMode('login');
      setIsAuthModalOpen(true);
      return;
    }

    // 校验评估模式
    if (enableJDMatch === null) {
      message.error('请选择评估模式');
      return;
    }

    // 定向模式校验
    if (enableJDMatch) {
      if (!jobName.trim()) {
        message.error('请输入岗位名称');
        return;
      }
      if (!jobLevel) {
        message.error('请选择岗位级别');
        return;
      }
      if (!jobDescription.trim()) {
        message.error('请输入岗位JD描述');
        return;
      }
    }

    setIsSubmitting(true);

    try {
      // 如果是PDF文件，渲染为图片（用于图片型PDF识别）
      let currentPicFile = picFile;
      if (isPdfFile(selectedFile)) {
        try {
          currentPicFile = await renderPdfToImage(selectedFile);
          setPicFile(currentPicFile);
        } catch (error) {
          console.error('PDF渲染失败:', error);
          message.error(
            error instanceof Error
              ? error.message
              : 'PDF处理失败，请尝试其他文件',
          );
          setIsSubmitting(false);
          return;
        }
      }

      const response = await optimizeResume({
        file: selectedFile,
        enableJDMatch,
        // PDF渲染的图片（用于图片型PDF识别）
        picFile: currentPicFile || undefined,
        // 定向模式参数
        jobName: enableJDMatch ? jobName.trim() : undefined,
        jobLevel: enableJDMatch ? jobLevel! : undefined,
        jobDescription: enableJDMatch ? jobDescription.trim() : undefined,
      });

      if (response.success) {
        setResult(response.data);
        setCurrentStep(Step.RESULT);
      } else {
        throw new Error('分析失败');
      }
    } catch (error) {
      message.error(
        error instanceof Error ? error.message : '分析失败，请稍后重试',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // 渲染上传步骤
  const renderUploadStep = () => (
    <div className="max-w-2xl mx-auto">
      {/* 标题 */}
      <div className="text-center mb-8">
        <h1 className="text-2xl font-bold text-white mb-2">
          <span className="bg-gradient-to-r from-white via-amber-100 to-amber-200 bg-clip-text text-transparent">
            简历评估
          </span>
        </h1>
        <p className="text-slate-400">AI 智能分析，给出专业评估建议</p>
      </div>

      {/* 上传区域 */}
      <div className="mb-6">
        <UploadArea
          onFileSelect={handleFileSelect}
          selectedFile={selectedFile}
          onRemoveFile={handleRemoveFile}
          disabled={isSubmitting}
        />
      </div>

      {/* 评估模式 */}
      <div className={`mb-6 ${isSubmitting ? 'opacity-60' : ''}`}>
        <label className="block text-slate-300 text-sm font-medium mb-3">
          评估模式
        </label>
        <div className="space-y-3">
          <button
            onClick={() => setEnableJDMatch(false)}
            disabled={isSubmitting}
            className={`w-full p-4 rounded-xl border text-left transition-all ${
              isSubmitting
                ? enableJDMatch === false
                  ? 'cursor-not-allowed border-amber-500 bg-amber-500/10'
                  : 'cursor-not-allowed border-slate-700 bg-slate-800/30'
                : enableJDMatch === false
                  ? 'cursor-pointer border-amber-500 bg-amber-500/10'
                  : 'cursor-pointer border-slate-700 bg-slate-800/50 hover:border-slate-600'
            }`}
          >
            <div className="flex items-center gap-3">
              <div
                className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                  enableJDMatch === false
                    ? 'border-amber-500'
                    : 'border-slate-600'
                }`}
              >
                {enableJDMatch === false && (
                  <div className="w-2.5 h-2.5 rounded-full bg-amber-500" />
                )}
              </div>
              <div>
                <p className="text-white font-medium">通用评估</p>
                <p className="text-slate-500 text-sm">分析简历本身的质量问题</p>
              </div>
            </div>
          </button>

          <button
            onClick={() => setEnableJDMatch(true)}
            disabled={isSubmitting}
            className={`w-full p-4 rounded-xl border text-left transition-all ${
              isSubmitting
                ? enableJDMatch === true
                  ? 'cursor-not-allowed border-amber-500 bg-amber-500/10'
                  : 'cursor-not-allowed border-slate-700 bg-slate-800/30'
                : enableJDMatch === true
                  ? 'cursor-pointer border-amber-500 bg-amber-500/10'
                  : 'cursor-pointer border-slate-700 bg-slate-800/50 hover:border-slate-600'
            }`}
          >
            <div className="flex items-center gap-3">
              <div
                className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                  enableJDMatch === true
                    ? 'border-amber-500'
                    : 'border-slate-600'
                }`}
              >
                {enableJDMatch === true && (
                  <div className="w-2.5 h-2.5 rounded-full bg-amber-500" />
                )}
              </div>
              <div>
                <p className="text-white font-medium">岗位定向评估</p>
                <p className="text-slate-500 text-sm">
                  结合目标岗位JD进行针对性评估
                </p>
              </div>
            </div>
          </button>
        </div>
      </div>

      {/* 定向模式：岗位名称、岗位级别、JD描述 */}
      <div
        className={`transition-all duration-300 ease-in-out overflow-hidden ${
          enableJDMatch === true
            ? 'max-h-[800px] opacity-100'
            : 'max-h-0 opacity-0'
        }`}
      >
        {/* 岗位名称 */}
        <div className={`mb-6 ${isSubmitting ? 'opacity-60' : ''}`}>
          <label className="block text-slate-300 text-sm font-medium mb-3">
            <span className="text-red-500 mr-1">*</span>
            岗位名称
            <span className="text-slate-500 text-xs ml-2">
              ({jobName.length}/30)
            </span>
          </label>
          <input
            type="text"
            value={jobName}
            onChange={(e) => {
              if (e.target.value.length <= 30) {
                setJobName(e.target.value);
              }
            }}
            maxLength={30}
            placeholder="请输入岗位名称..."
            disabled={isSubmitting}
            className="w-full px-4 py-3 bg-slate-800/50 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-amber-500 disabled:cursor-not-allowed"
          />
        </div>

        {/* 岗位级别 */}
        <div className={`mb-6 ${isSubmitting ? 'opacity-60' : ''}`}>
          <label className="block text-slate-300 text-sm font-medium mb-3">
            <span className="text-red-500 mr-1">*</span>
            岗位级别
          </label>
          <div className="flex gap-3">
            {(['junior', 'mid', 'senior'] as TargetLevel[]).map((level) => (
              <button
                key={level}
                onClick={() => setJobLevel(level)}
                disabled={isSubmitting}
                className={`flex-1 py-3 px-4 rounded-xl border transition-all ${
                  isSubmitting
                    ? 'cursor-not-allowed border-slate-700 bg-slate-800/30 text-slate-500'
                    : jobLevel === level
                      ? 'cursor-pointer border-amber-500 bg-amber-500/20 text-white'
                      : 'cursor-pointer border-slate-700 bg-slate-800/50 text-slate-400 hover:border-slate-600'
                }`}
              >
                {getTargetLevelText(level)}
              </button>
            ))}
          </div>
        </div>

        {/* 岗位JD */}
        <div className={`mb-6 ${isSubmitting ? 'opacity-60' : ''}`}>
          <label className="block text-slate-300 text-sm font-medium mb-3">
            <span className="text-red-500 mr-1">*</span>
            岗位JD（职位描述）
            <span className="text-slate-500 text-xs ml-2">
              ({jobDescription.length}/600)
            </span>
          </label>
          <textarea
            value={jobDescription}
            onChange={(e) => {
              if (e.target.value.length <= 600) {
                setJobDescription(e.target.value);
              }
            }}
            maxLength={600}
            placeholder="请粘贴目标岗位的职位描述..."
            rows={6}
            disabled={isSubmitting}
            className="w-full px-4 py-3 bg-slate-800/50 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-amber-500 resize-none disabled:cursor-not-allowed"
          />
        </div>
      </div>

      {/* 开始评估按钮 */}
      <div className="flex justify-center">
        <button
          onClick={handleStartAnalyze}
          disabled={
            isSubmitting ||
            !selectedFile ||
            enableJDMatch === null ||
            (enableJDMatch === true &&
              (!jobName.trim() || !jobLevel || !jobDescription.trim()))
          }
          className={`
            relative w-[200px] py-3 rounded-xl font-medium text-base overflow-hidden
            transition-all duration-300
            ${
              selectedFile &&
              !isSubmitting &&
              enableJDMatch !== null &&
              !(
                enableJDMatch === true &&
                (!jobName.trim() || !jobLevel || !jobDescription.trim())
              )
                ? 'bg-gradient-to-r from-amber-500 to-amber-600 shadow-lg shadow-amber-500/30 hover:shadow-amber-500/50 hover:scale-[1.02] animate-gradient cursor-pointer'
                : 'bg-slate-700 cursor-not-allowed'
            }
          `}
        >
          <span
            className={`relative z-10 flex items-center justify-center ${
              selectedFile &&
              !isSubmitting &&
              enableJDMatch !== null &&
              !(
                enableJDMatch === true &&
                (!jobName.trim() || !jobLevel || !jobDescription.trim())
              )
                ? 'text-white'
                : 'text-slate-500'
            }`}
          >
            {isSubmitting ? (
              <>
                <div className="w-4 h-4 border-2 border-slate-500 border-t-transparent rounded-full animate-spin" />
                <span className="ml-2">评估中...</span>
              </>
            ) : (
              <span>开始评估</span>
            )}
          </span>
          {selectedFile &&
            !isSubmitting &&
            enableJDMatch !== null &&
            !(
              enableJDMatch === true &&
              (!jobName.trim() || !jobLevel || !jobDescription.trim())
            ) && (
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full animate-shimmer" />
            )}
        </button>
      </div>
    </div>
  );

  // 渲染结果步骤
  const renderResultStep = () => {
    if (!result) return null;

    // 判断是否为岗位定向分析模式（根据用户选择的模式判断）
    const isJDMode = enableJDMatch === true;

    return (
      <div className="max-w-3xl mx-auto">
        {/* 头部标题和返回按钮 */}
        <div className="relative mb-8">
          <button
            onClick={() => setCurrentStep(Step.UPLOAD)}
            className="absolute left-0 top-1/2 -translate-y-1/2 flex items-center gap-1 text-slate-400 hover:text-white transition-colors cursor-pointer"
          >
            <ChevronLeft className="w-5 h-5" />
            <span>返回</span>
          </button>
          <h1 className="text-2xl font-bold text-white text-center">
            <span className="bg-gradient-to-r from-white via-amber-100 to-amber-200 bg-clip-text text-transparent">
              分析结果
            </span>
          </h1>
        </div>

        {/* 评分 */}
        <div className="mb-6">
          <ScoreDisplay scores={result.scores} isJDMode={isJDMode} />
        </div>

        {/* 岗位定向分析模式：岗位匹配分析放在第一个 */}
        {isJDMode && result.jdAnalysis && (
          <div className="mb-6">
            <JDAnalysis analysis={result.jdAnalysis} />
          </div>
        )}

        {/* 主要问题 */}
        <div className="mb-6">
          <IssuesList issues={result.mainIssues} />
        </div>

        {/* 优化建议 */}
        <div className="mb-6">
          <SuggestionsList suggestions={result.suggestions} />
        </div>

        {/* 通用模式：岗位匹配分析放在最后（如果有的话） */}
        {!isJDMode && result.jdAnalysis && (
          <div className="mb-6">
            <JDAnalysis analysis={result.jdAnalysis} />
          </div>
        )}

        {/* 底部操作按钮 */}
        <div className="flex justify-center mt-8">
          {/* 编辑简历按钮 - 支持 txt/md/docx/pdf，样式与开始评估按钮保持一致 */}
          {selectedFile && (
            <button
              onClick={() => setShowEditor(true)}
              className="relative w-[200px] py-3 rounded-xl font-medium text-base overflow-hidden transition-all duration-300 bg-gradient-to-r from-amber-500 to-amber-600 shadow-lg shadow-amber-500/30 hover:shadow-amber-500/50 hover:scale-[1.02] animate-gradient cursor-pointer"
            >
              <span className="relative z-10 flex items-center justify-center gap-2 text-white">
                <Edit3 className="w-4 h-4" />
                编辑简历
              </span>
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full animate-shimmer" />
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-[#0B0F19] via-[#0F172A] to-[#1E293B] py-8 px-4"
      style={{ minHeight: '73vh' }}
    >
      <div className="max-w-4xl mx-auto mt-16">
        {currentStep === Step.UPLOAD && renderUploadStep()}
        {currentStep === Step.RESULT && renderResultStep()}
      </div>

      {/* 简历编辑器模态框 */}
      <ResumeEditorModal
        file={selectedFile}
        isOpen={showEditor}
        onClose={() => setShowEditor(false)}
        evaluationResult={result}
      />

      {/* 登录/注册弹窗 */}
      <AuthModal
        isOpen={isAuthModalOpen}
        onClose={() => setIsAuthModalOpen(false)}
        defaultMode={authModalMode}
      />

      {/* 动画样式 */}
      <style>{`
        @keyframes shimmer {
          100% {
            transform: translateX(100%);
          }
        }

        @keyframes gradient {
          0%, 100% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
        }

        .animate-shimmer {
          animation: shimmer 2s infinite;
        }

        .animate-gradient {
          background-size: 200% 200%;
          animation: gradient 3s ease infinite;
        }

        @keyframes slide-down {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-slide-down {
          animation: slide-down 0.3s ease-out forwards;
        }

        /* 编辑器弹窗样式覆盖 */
        .resume-editor-modal .ant-modal-content {
          background: #0F172A !important;
          border: 1px solid #334155 !important;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5) !important;
        }

        .resume-editor-modal.fullscreen-modal .ant-modal-content {
          border: none !important;
          border-radius: 0 !important;
          height: 100vh !important;
          max-height: 100vh !important;
        }

        .resume-editor-modal .ant-modal-body {
          background: #0F172A !important;
          padding: 0 !important;
        }

        .resume-editor-modal.fullscreen-modal .ant-modal-body {
          height: 100vh !important;
          overflow: hidden !important;
        }



        /* MDEditor 暗色主题适配 */
        .w-md-editor {
          background-color: #0B0F19 !important;
          border: none !important;
        }

        .w-md-editor-content {
          background-color: #0B0F19 !important;
        }

        .w-md-editor-text-pre,
        .w-md-editor-text-input {
          color: #e2e8f0 !important;
        }

        .w-md-editor-toolbar {
          background-color: #1e293b !important;
          border-bottom: 1px solid #334155 !important;
        }

        .w-md-editor-toolbar button {
          color: #94a3b8 !important;
        }

        .w-md-editor-toolbar button:hover {
          color: #e2e8f0 !important;
          background-color: #334155 !important;
        }
      `}</style>
    </div>
  );
}
